import { useId, type ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fmtNumber, fmtPercent } from '@/lib/format';
import type { OverviewPoint } from '@/lib/reports';
import { cn } from '@/lib/utils';

/*
  Overview charts follow the dataviz method the job home already uses: one focal hue (--chart-1, the tenant's
  primary), failed / opt-out marks in the destructive colour with a 45° hatch so the pair never relies on colour
  alone, solid hairline grid, thin columns with a card-coloured gap, a hover tooltip on every mark, and a table
  twin under each chart so nothing is gated behind hover.
*/

const AXIS = { fontSize: 11, fill: 'var(--muted-foreground)' } as const;

export function shortDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d);
}
export function longDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'long', day: 'numeric' }).format(d);
}

/** Recessive pattern for the "bad" series; unique id per instance so several charts can share a page. */
function Hatch({ id }: { id: string }) {
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="var(--card)" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="var(--destructive)" strokeWidth="2.5" />
      </pattern>
    </defs>
  );
}

export function Swatch({ kind }: { kind: 'primary' | 'hatch' | 'muted' }) {
  if (kind === 'hatch') return <span className="inline-block size-2.5 border" style={{ background: 'repeating-linear-gradient(45deg, var(--destructive) 0 1.5px, transparent 1.5px 3.5px)', borderColor: 'var(--destructive)' }} aria-hidden />;
  return <span className="inline-block size-2.5" style={{ background: kind === 'primary' ? 'var(--chart-1)' : 'var(--chart-5)' }} aria-hidden />;
}

export function Legend({ items }: { items: Array<{ kind: 'primary' | 'hatch' | 'muted'; label: string }> }) {
  return (
    <ul className="text-muted-foreground flex flex-wrap items-center gap-4 text-xs" aria-label="Legend">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-1.5">
          <Swatch kind={i.kind} /> {i.label}
        </li>
      ))}
    </ul>
  );
}

function Tip({ title, rows }: { title: string; rows: Array<{ label: string; value: string; muted?: boolean }> }) {
  return (
    <div className="bg-popover text-popover-foreground border px-3 py-2 text-xs shadow-sm">
      <div className="text-muted-foreground mb-1">{title}</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 tabular-nums">
        {rows.map((r) => (
          <div key={r.label} className="contents">
            <span className={cn(r.muted && 'text-muted-foreground')}>{r.label}</span>
            <span className={cn('text-right font-medium', r.muted && 'text-muted-foreground font-normal')}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The WCAG twin of a chart: every plotted value, reachable without hover. */
export function ChartTable({ caption, head, rows }: { caption: string; head: string[]; rows: Array<Array<string | number>> }) {
  return (
    <details className="text-muted-foreground mt-3 text-xs">
      <summary className="hover:text-foreground cursor-pointer select-none underline-offset-4 hover:underline">Table view</summary>
      <div className="mt-2 max-h-64 overflow-y-auto border">
        <table className="w-full">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left">
              {head.map((h, i) => (
                <th key={h} className={cn('px-2 py-1 font-medium', i > 0 && 'text-right')}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                {r.map((c, j) => (
                  <td key={j} className={cn('px-2 py-1', j === 0 ? 'text-foreground' : 'text-right tabular-nums')}>
                    {typeof c === 'number' ? fmtNumber(c) : c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function tickInterval(n: number): number {
  return Math.max(0, Math.ceil(n / 8) - 1);
}

/** Delivered under failed, per day. */
export function DeliveriesChart({ series, height = 240 }: { series: OverviewPoint[]; height?: number }) {
  const id = useId().replace(/:/g, '');
  const rows = series.map((d) => ({ ...d, label: shortDay(d.day) }));
  const quiet = rows.every((r) => r.delivered === 0 && r.failed === 0);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Legend items={[{ kind: 'primary', label: 'Delivered' }, { kind: 'hatch', label: 'Failed' }]} />
        {quiet && <span className="text-muted-foreground text-xs">No texts went out in this period.</span>}
      </div>
      <div style={{ height }} role="img" aria-label="Texts delivered and failed per day">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 4, bottom: 0, left: -12 }} barCategoryGap="30%" maxBarSize={24}>
            <Hatch id={`hatch-${id}`} />
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS} interval={tickInterval(rows.length)} />
            <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={44} tick={AXIS} tickFormatter={(v: number) => fmtNumber(v)} />
            <Tooltip
              cursor={{ fill: 'var(--accent)', opacity: 0.6 }}
              content={({ active, payload }) => {
                const p = payload?.[0]?.payload as OverviewPoint | undefined;
                if (!active || !p) return null;
                const attempted = p.delivered + p.failed;
                return <Tip title={longDay(p.day)} rows={[{ label: 'Delivered', value: fmtNumber(p.delivered) }, { label: 'Failed', value: fmtNumber(p.failed) }, { label: 'Delivery rate', value: attempted ? fmtPercent((p.delivered / attempted) * 100) : '—', muted: true }, { label: 'Messages', value: fmtNumber(p.messages), muted: true }]} />;
              }}
            />
            <Bar dataKey="delivered" stackId="a" fill="var(--chart-1)" stroke="var(--card)" strokeWidth={2} name="Delivered" isAnimationActive={false} />
            <Bar dataKey="failed" stackId="a" fill={`url(#hatch-${id})`} stroke="var(--card)" strokeWidth={2} name="Failed" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartTable caption="Texts delivered and failed per day" head={['Day', 'Messages', 'Delivered', 'Failed']} rows={rows.filter((r) => r.messages || r.delivered || r.failed).map((r) => [longDay(r.day), r.messages, r.delivered, r.failed])} />
    </div>
  );
}

/** Contacts added above the line, opt-outs below: polarity, so a diverging column chart. */
export function ListChangesChart({ series, height = 200 }: { series: OverviewPoint[]; height?: number }) {
  const id = useId().replace(/:/g, '');
  const rows = series.map((d) => ({ ...d, label: shortDay(d.day), optOutsNeg: -d.optOuts }));
  const quiet = rows.every((r) => r.contactsAdded === 0 && r.optOuts === 0);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Legend items={[{ kind: 'primary', label: 'Contacts added' }, { kind: 'hatch', label: 'Opt-outs' }]} />
        {quiet && <span className="text-muted-foreground text-xs">No list changes in this period.</span>}
      </div>
      <div style={{ height }} role="img" aria-label="Contacts added and opt-outs per day">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} stackOffset="sign" margin={{ top: 8, right: 4, bottom: 0, left: -12 }} barCategoryGap="30%" maxBarSize={24}>
            <Hatch id={`hatch-${id}`} />
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS} interval={tickInterval(rows.length)} />
            <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={44} tick={AXIS} tickFormatter={(v: number) => fmtNumber(Math.abs(v))} />
            <ReferenceLine y={0} stroke="var(--foreground)" strokeOpacity={0.4} />
            <Tooltip
              cursor={{ fill: 'var(--accent)', opacity: 0.6 }}
              content={({ active, payload }) => {
                const p = payload?.[0]?.payload as OverviewPoint | undefined;
                if (!active || !p) return null;
                return <Tip title={longDay(p.day)} rows={[{ label: 'Added', value: fmtNumber(p.contactsAdded) }, { label: 'Opted out', value: fmtNumber(p.optOuts) }, { label: 'Net', value: `${p.contactsAdded - p.optOuts >= 0 ? '+' : ''}${fmtNumber(p.contactsAdded - p.optOuts)}`, muted: true }]} />;
              }}
            />
            <Bar dataKey="contactsAdded" stackId="a" fill="var(--chart-1)" stroke="var(--card)" strokeWidth={2} name="Added" isAnimationActive={false} />
            <Bar dataKey="optOutsNeg" stackId="a" fill={`url(#hatch-${id})`} stroke="var(--card)" strokeWidth={2} name="Opt-outs" isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartTable caption="Contacts added and opt-outs per day" head={['Day', 'Added', 'Opt-outs']} rows={rows.filter((r) => r.contactsAdded || r.optOuts).map((r) => [longDay(r.day), r.contactsAdded, r.optOuts])} />
    </div>
  );
}

export interface RankedRow {
  key: string;
  label: ReactNode;
  value: number;
  /** Right-aligned text beside the value (e.g. a delivery rate). */
  meta?: string;
  /** Small line under the bar. */
  hint?: string;
}

/** Magnitude across a handful of named things (jobs, keywords): one hue, bars from a shared baseline, values at the tip. */
export function RankedBars({ rows, unit, emptyText = 'Nothing to rank in this period.' }: { rows: RankedRow[]; unit?: string; emptyText?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0) return <p className="text-muted-foreground text-sm">{emptyText}</p>;
  return (
    <ol className="space-y-3">
      {rows.map((r) => (
        <li key={r.key} className="grid gap-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium">{r.label}</span>
            <span className="shrink-0 tabular-nums">
              {fmtNumber(r.value)}
              {unit ? <span className="text-muted-foreground"> {unit}</span> : null}
              {r.meta ? <span className="text-muted-foreground ml-2 text-xs">{r.meta}</span> : null}
            </span>
          </div>
          <div className="bg-muted h-2 w-full" aria-hidden>
            <div className="h-full" style={{ width: `${Math.max(r.value > 0 ? 2 : 0, (r.value / max) * 100)}%`, background: 'var(--chart-1)' }} />
          </div>
          {r.hint && <div className="text-muted-foreground text-xs">{r.hint}</div>}
        </li>
      ))}
    </ol>
  );
}

/** 24px sparkline: area wash + 2px line + end dot with a card ring. Flat baseline when nothing happened. */
export function Sparkline({ points, className, bad = false }: { points: number[]; className?: string; bad?: boolean }) {
  const w = 120;
  const h = 28;
  const pad = 3;
  const n = points.length;
  if (n === 0) return <div className={cn('h-7', className)} />;
  const max = Math.max(...points);
  const min = Math.min(0, ...points);
  const range = max - min || 1;
  const x = (i: number) => (n === 1 ? w / 2 : pad + (i / (n - 1)) * (w - pad * 2));
  const y = (v: number) => h - pad - ((v - min) / range) * (h - pad * 2);
  const path = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${path} L${x(n - 1).toFixed(1)},${(h - pad).toFixed(1)} L${x(0).toFixed(1)},${(h - pad).toFixed(1)} Z`;
  const color = bad ? 'var(--destructive)' : 'var(--chart-1)';
  const flat = max === 0;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cn('h-7 w-full', className)} aria-hidden>
      {!flat && <path d={area} fill={color} fillOpacity={0.1} />}
      <path d={path} fill="none" stroke={flat ? 'var(--border)' : color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {!flat && (
        <>
          <circle cx={x(n - 1)} cy={y(points[n - 1] as number)} r={4.5} fill="var(--card)" />
          <circle cx={x(n - 1)} cy={y(points[n - 1] as number)} r={3} fill={color} />
        </>
      )}
    </svg>
  );
}
